import React from "react";

/**
 * Infinite horizontal marquee of team mini-photos.
 * Cards are styled as continuous vertical filmstrip cells with gaps.
 */
export default function Team({ site, members = [] }) {
  const list = members.length ? members : [];

  return (
    <section
      data-testid="team-section"
      id="team"
      className="bg-[#050505] border-t border-white/10"
    >
      <div className="px-5 sm:px-8 lg:px-12 pt-20 lg:pt-24 pb-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="overline">Команда</p>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl uppercase leading-[1] tracking-tight">
              {site.team_title || "Кто стоит за кадром"}
            </h2>
          </div>
          <p className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/40 max-w-md text-right">
            Постоянная команда продюсеров, художников и звукорежиссёров.
          </p>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-white/40 px-5 sm:px-8 lg:px-12 pb-20">Ещё нет добавленных участников.</p>
      ) : (
        <div className="pb-20 lg:pb-24">
          <TeamRow members={list} direction="ltr" duration={Math.max(80, list.length * 10)} testId="team-row-0" />
        </div>
      )}
    </section>
  );
}

function TeamRow({ members, direction, duration, testId }) {
  // Вычисляем, сколько раз нужно продублировать массив, чтобы лента гарантированно 
  // перекрывала любой широкий монитор. Это уберет "скачок" анимации.
  const copies = Math.max(1, Math.ceil(12 / members.length));
  const half = Array(copies).fill(members).flat();
  
  // Создаем две абсолютно идентичные половины для идеального translateX(-50%)
  const sequence = [...half, ...half];
  
  return (
    <div data-testid={testId} className="overflow-hidden py-4 border-y border-white/10">
      <div
        className={`bbf-marquee-track flex ${direction === "rtl" ? "bbf-marquee-rev" : ""}`}
        style={{ animationDuration: `${duration}s` }}
      >
        {sequence.map((m, i) => (
          // pr-4/pr-6 (padding-right) создает безопасный отступ, который не ломает математику 50% анимации
          <div key={`${m.id || m.name}-${i}`} className="pr-4 sm:pr-6 shrink-0">
            <figure
              className="flex flex-col relative w-[240px] sm:w-[280px] h-[440px] sm:h-[480px] border border-white/10 group bg-[#050505] whitespace-normal"
            >
              {/* Верхняя часть: Фото с градиентом */}
              <div className="relative h-[55%] sm:h-[60%] w-full overflow-hidden shrink-0">
                {m.photo_url ? (
                  <img
                    src={m.photo_url}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover grayscale opacity-85 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 bg-white/5" />
                )}
                {/* Плавный переход в черный фон снизу */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />
              </div>

              {/* Нижняя часть: Текстовый блок */}
              <div className="flex-1 p-5 sm:p-6 pt-0 flex flex-col bg-[#050505]">
                <p className="font-display text-[20px] sm:text-[22px] leading-[1.05] text-[#a78bfa] mb-3 break-words">
                  {m.name.split(" ").map((word, index) => (
                    <span key={index} className="block">
                      {word}
                    </span>
                  ))}
                </p>
                <div className="text-[12px] sm:text-[13px] leading-[1.4] text-white/70">
                  {m.role && <span className="block mb-1 text-white/90">{m.role}</span>}
                  {m.note && <span className="block whitespace-pre-line text-white/50">{m.note}</span>}
                </div>
              </div>
            </figure>
          </div>
        ))}
      </div>
    </div>
  );
}